import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener('change', this.ftInstantPrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener('change', this.ftInstantPrice);
  }

  /**
   * Fabric Tales 2026-10-16: show the chosen variant's price immediately from the
   * data-ft-variant-prices map (snippets/price.liquid). The theme still re-renders the price from the
   * server a moment later (updatePrice), so this only removes the wait; it never has the last word.
   * @param {Event} event
   */
  ftInstantPrice = (event) => {
    const target = event.target;
    const source = target instanceof HTMLSelectElement ? target.selectedOptions[0] : target;
    const variantId = source instanceof HTMLElement ? source.dataset.variantId : null;
    if (!variantId) return;
    const data = this.querySelector('script[data-ft-variant-prices]');
    if (!data) return;
    let map;
    try {
      map = JSON.parse(data.textContent);
    } catch (e) {
      return;
    }
    const entry = map[variantId];
    if (!entry) return;
    const [price, compare, onSale] = entry;
    const box = this.querySelector('[ref="priceContainer"]');
    const regular = box?.querySelector('.price__regular');
    const sale = box?.querySelector('.price__sale');
    if (!regular || !sale) return;
    regular.querySelector('.price')?.replaceChildren(price);
    sale.querySelector('.price-item--sale')?.replaceChildren(price);
    sale.querySelector('.compare-at-price')?.replaceChildren(compare);
    regular.classList.toggle('price__hidden', !!onSale);
    sale.classList.toggle('price__hidden', !onSale);
  };

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Fabric Tales 2026-10-16: look the elements up fresh. The cached this.refs entries go stale after the
    // first replaceWith() inside the quick-add dialog, so later variant changes never updated the price there.
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    const volumePricingNote = this.querySelector('[ref="volumePricingNote"]');
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
